import React, { useState } from 'react';
import { Button } from 'components/Buttons/Button';
import Typography from 'components/Typography/Typography';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { definedTranslation } from '../../i18n';
import {
  currentColorwayIdSelector,
  selectedChildMaterialSelector,
} from '../../redux/selectors/materialsSelector';
import { FinishesGroupData } from '../../types/material';
import { CurrentConfigData, SelectboxConfig } from '../../types/quickView';
import { CardWrapperProps, CardWrapper } from '../Card/CardWrapper';
import { FinishesConfig } from '../FinishesConfig';
import { FinishesGroupSkeleton } from './FinishesGroupSkeleton';

interface FinishesGroupProps
  extends Omit<CardWrapperProps, 'cardIndex' | 'groupFinishes'> {
  isLoading: boolean;
  item: FinishesGroupData;
  groupIndex: number;
  showAll?: boolean;
  isColorways?: boolean;
  collapseLimit?: number;
  loadingCartIds?: string[];
  configData?: SelectboxConfig[];
  currentConfig?: CurrentConfigData[];
  onFilterChange?: (value: string, attribute: string) => void;
}

export const FinishesGroup: React.FC<FinishesGroupProps> = ({
  item,
  isLoading,
  clickedProductGridIndex,
  activeProduct,
  projects,
  currentProjectData,
  cartItemsIds,
  wasModalTriggered,
  onProductClick,
  onAddToCart,
  showAll = false,
  isColorways,
  collapseLimit = 4,
  loadingCartIds,
  currentConfig,
  configData,
  onFilterChange,
}) => {
  const intl = useIntl();
  const selectedChildMaterial = useSelector(selectedChildMaterialSelector);
  const selectedColorwayId = useSelector(currentColorwayIdSelector);
  const groupFinishes = item?.items || [];
  const [isCollapsed, setIsCollapsed] = useState(false);

  const onCollapse = () => {
    setIsCollapsed((prev) => !prev);
  };

  const getTranslatedTitle = (title: string) => {
    // Convert title to a suitable key format by removing spaces
    const titleId = title.replace(/\s+/g, '');

    try {
      return intl?.formatMessage({ id: titleId, defaultMessage: title });
    } catch {
      // If translation fails, return the original title
      return title;
    }
  };

  if (isLoading) {
    return <FinishesGroupSkeleton />;
  }

  if (groupFinishes?.length === 0 && !isLoading) {
    return null;
  }

  const labels = {
    more: intl?.formatMessage(definedTranslation.miniPDP.more),
    collapse: intl?.formatMessage(definedTranslation.miniPDP.collapse),
  };

  return (
    <div key={item.title} className="flex flex-col gap-1 w-full">
      <div className="flex flex-row justify-between items-center w-full">
        <Typography
          variant="h2"
          fontSize="20px"
          fontWeight="600"
          color="primary"
        >
          {getTranslatedTitle(item.title)}
        </Typography>
        {configData && configData?.length > 0 && (
          <FinishesConfig
            configData={configData}
            currentConfig={currentConfig}
            onFilterChange={onFilterChange}
            intl={intl}
          />
        )}
        {item?.items?.length > collapseLimit && !showAll && (
          <Button
            iconDense
            color="secondary"
            variant="contained"
            size="small"
            endIcon={isCollapsed ? 'minus' : 'plus'}
            onClick={() => onCollapse()}
          >
            {isCollapsed
              ? labels.collapse
              : `${item?.items?.length - collapseLimit} ${labels.more}`}
          </Button>
        )}
      </div>
      <div className="flex flex-wrap">
        {Array.from({
          length: isCollapsed
            ? groupFinishes.length
            : Math.min(collapseLimit, groupFinishes.length),
        }).map((_, index) => (
          <div style={cardStyle} key={groupFinishes[index]?.entityId}>
            <CardWrapper
              cardIndex={index}
              isColorways={isColorways}
              groupFinishes={groupFinishes}
              clickedProductGridIndex={clickedProductGridIndex}
              activeProduct={activeProduct}
              projects={projects}
              currentProjectData={currentProjectData}
              cartItemsIds={cartItemsIds}
              wasModalTriggered={wasModalTriggered}
              onProductClick={onProductClick}
              onAddToCart={onAddToCart}
              currentMaterialId={
                selectedChildMaterial?.entityId || selectedColorwayId
              }
              loadingCartIds={loadingCartIds}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

////
const cardStyle = {
  width: '20%',
  padding: '5px',
};
